import { useEffect, useMemo, useRef, useState } from "react";

export const BREAK_TIME_GAME_OPTIONS = [
  {
    id: "tic_tac_toe",
    label: "Tic Tac Toe",
    shortLabel: "XO",
    description: "Draw O and X in the grid."
  },
  {
    id: "dots_and_boxes",
    label: "Dots & Boxes",
    shortLabel: "DB",
    description: "Join dots and close boxes."
  }
];

const TIC_TAC_TOE = "tic_tac_toe";
const DOTS_AND_BOXES = "dots_and_boxes";
const COMMITTED_STROKE_WIDTH = 2.35;
const DRAFT_STROKE_WIDTH = 2.05;
const LIVE_STROKE_WIDTH = 2.05;
const DOTS_GRID_COUNT = 4;
const DOTS_GRID_PADDING = 14;

const TIC_TAC_TOE_WIN_LINES = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 4, 6],
  [0, 4, 8],
  [2, 4, 6]
];

function nowIso() {
  return new Date().toISOString();
}

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, Number(value) || 0));
}

function distance(x1, y1, x2, y2) {
  return Math.hypot(x2 - x1, y2 - y1);
}

function sanitizeStroke(stroke) {
  const points = Array.isArray(stroke?.points)
    ? stroke.points.map(Number).filter(Number.isFinite).slice(0, 280)
    : [];
  return { points };
}

function buildStrokePath(points) {
  if (!Array.isArray(points) || points.length < 4) return "";
  let path = `M ${points[0] * 100} ${points[1] * 100}`;
  for (let index = 2; index < points.length; index += 2) {
    path += ` L ${points[index] * 100} ${points[index + 1] * 100}`;
  }
  return path;
}

function getMarkerColor(marker) {
  return marker === "X" ? "#0f172a" : "#1d4ed8";
}

function getDraftStrokeColor(marker, alpha = 0.64) {
  return marker === "X"
    ? `rgba(15, 23, 42, ${alpha})`
    : `rgba(37, 99, 235, ${alpha})`;
}

function getParticipantInitial(name) {
  const source = String(name || "").trim().toUpperCase();
  const match = source.match(/[A-Z0-9]/);
  return match ? match[0] : "S";
}

function getParticipantById(userId, participants) {
  return (Array.isArray(participants) ? participants : []).find(
    (participant) => String(participant?.userId || "") === String(userId || "")
  ) || null;
}

function getParticipantByRole(role, ownerUserId, participants) {
  const list = Array.isArray(participants) ? participants : [];
  if (role === "owner") {
    return list.find((participant) => String(participant?.userId || "") === String(ownerUserId || "")) || list[0] || null;
  }
  return list.find((participant) => String(participant?.userId || "") !== String(ownerUserId || "")) || list[1] || list[0] || null;
}

function getParticipantName(userId, participants) {
  return getParticipantById(userId, participants)?.name || "Student";
}

function getParticipantNameByRole(role, ownerUserId, participants) {
  return getParticipantByRole(role, ownerUserId, participants)?.name || "Student";
}

function getParticipantColorByRole(role, ownerUserId, participants, fallbackMarker = "O") {
  return getParticipantByRole(role, ownerUserId, participants)?.color || getMarkerColor(fallbackMarker);
}

function getPlayerRole(ownerUserId, currentUserId) {
  return String(ownerUserId || "") === String(currentUserId || "") ? "owner" : "guest";
}

function getMarkerForRole(role) {
  return role === "owner" ? "O" : "X";
}

function getStrokeMetrics(points) {
  if (!Array.isArray(points) || points.length < 4) return null;
  const pairs = [];
  for (let index = 0; index < points.length; index += 2) {
    pairs.push({ x: points[index], y: points[index + 1] });
  }
  if (pairs.length < 2) return null;

  let minX = pairs[0].x;
  let maxX = pairs[0].x;
  let minY = pairs[0].y;
  let maxY = pairs[0].y;
  let pathLength = 0;
  let centroidX = 0;
  let centroidY = 0;

  pairs.forEach((point, index) => {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
    centroidX += point.x;
    centroidY += point.y;
    if (index > 0) {
      pathLength += distance(pairs[index - 1].x, pairs[index - 1].y, point.x, point.y);
    }
  });

  centroidX /= pairs.length;
  centroidY /= pairs.length;

  const start = pairs[0];
  const end = pairs[pairs.length - 1];
  const width = Math.max(0.001, maxX - minX);
  const height = Math.max(0.001, maxY - minY);
  const diagonal = Math.hypot(width, height);
  const directLength = distance(start.x, start.y, end.x, end.y);
  const radiusValues = pairs.map((point) => distance(point.x, point.y, centroidX, centroidY));
  const radiusMean = radiusValues.reduce((sum, value) => sum + value, 0) / radiusValues.length;
  const radiusVariance = radiusValues.reduce((sum, value) => sum + Math.pow(value - radiusMean, 2), 0) / radiusValues.length;
  const radiusStd = Math.sqrt(radiusVariance);

  return {
    start,
    end,
    minX,
    maxX,
    minY,
    maxY,
    width,
    height,
    diagonal,
    pathLength,
    directLength,
    centroidX,
    centroidY,
    radiusMean,
    radiusStd,
    aspectRatio: width / height,
    lineRatio: pathLength > 0 ? directLength / pathLength : 0
  };
}

function cloneBoard(board) {
  return Array.isArray(board) ? board.slice(0, 9).concat(Array(Math.max(0, 9 - board.length)).fill(null)).slice(0, 9) : Array.from({ length: 9 }, () => null);
}

function sanitizeBoardCell(cell) {
  if (!cell || typeof cell !== "object") return null;
  const marker = String(cell.marker || "").toUpperCase();
  if (!["O", "X"].includes(marker)) return null;
  return {
    marker,
    userId: String(cell.userId || ""),
    userName: String(cell.userName || ""),
    color: String(cell.color || getMarkerColor(marker)),
    createdAt: cell.createdAt || nowIso(),
    strokes: (Array.isArray(cell.strokes) ? cell.strokes : []).map(sanitizeStroke).filter((stroke) => stroke.points.length >= 4)
  };
}

function computeTicTacToeWinner(board) {
  for (const line of TIC_TAC_TOE_WIN_LINES) {
    const [a, b, c] = line;
    const first = board[a];
    if (!first?.marker) continue;
    if (board[b]?.marker === first.marker && board[c]?.marker === first.marker) {
      return { winner: first.marker, winningLine: line };
    }
  }
  if (board.every(Boolean)) {
    return { winner: "draw", winningLine: [] };
  }
  return { winner: null, winningLine: [] };
}

function createEmptyTicTacToeState(round = 1, updatedAt = "") {
  return {
    round,
    board: Array.from({ length: 9 }, () => null),
    currentTurn: "O",
    winner: null,
    winningLine: [],
    moveCount: 0,
    updatedAt,
    lastMoveBy: "",
    lastMoveCell: null
  };
}

function normalizeTicTacToeState(input) {
  const source = input && typeof input === "object" ? input : {};
  const board = cloneBoard(source.board).map(sanitizeBoardCell);
  const winnerState = computeTicTacToeWinner(board);
  return {
    round: Math.max(1, Number(source.round || 1)),
    board,
    currentTurn: source.currentTurn === "X" ? "X" : "O",
    winner: winnerState.winner,
    winningLine: winnerState.winningLine,
    moveCount: board.filter(Boolean).length,
    updatedAt: String(source.updatedAt || ""),
    lastMoveBy: String(source.lastMoveBy || ""),
    lastMoveCell: Number.isFinite(Number(source.lastMoveCell)) ? Number(source.lastMoveCell) : null
  };
}

function detectOMarker(strokes) {
  if (!Array.isArray(strokes) || strokes.length !== 1) {
    return { accepted: false, hint: "Draw one rounded loop for O." };
  }
  const metrics = getStrokeMetrics(strokes[0]?.points || []);
  if (!metrics) {
    return { accepted: false, hint: "Draw a larger loop for O." };
  }

  const closeLoop = distance(metrics.start.x, metrics.start.y, metrics.end.x, metrics.end.y) <= metrics.diagonal * 0.34;
  const shapeBalanced = metrics.aspectRatio >= 0.55 && metrics.aspectRatio <= 1.7;
  const largeEnough = metrics.width >= 0.22 && metrics.height >= 0.22;
  const enoughTravel = metrics.pathLength >= Math.max(metrics.width, metrics.height) * 1.65;
  const smoothLoop = metrics.radiusMean > 0 && metrics.radiusStd / metrics.radiusMean <= 0.5;

  if (closeLoop && shapeBalanced && largeEnough && enoughTravel && smoothLoop) {
    return { accepted: true };
  }

  return { accepted: false, hint: "That looked open. Try one smooth closed loop for O." };
}

function getDiagonalDirection(metrics) {
  const dx = metrics.end.x - metrics.start.x;
  const dy = metrics.end.y - metrics.start.y;
  if (Math.abs(dx) < 0.08 || Math.abs(dy) < 0.08) return 0;
  return dx * dy >= 0 ? 1 : -1;
}

function lineIntersection(pointA1, pointA2, pointB1, pointB2) {
  const denominator =
    (pointA1.x - pointA2.x) * (pointB1.y - pointB2.y) -
    (pointA1.y - pointA2.y) * (pointB1.x - pointB2.x);
  if (Math.abs(denominator) < 0.0001) return null;

  const numeratorX =
    (pointA1.x * pointA2.y - pointA1.y * pointA2.x) * (pointB1.x - pointB2.x) -
    (pointA1.x - pointA2.x) * (pointB1.x * pointB2.y - pointB1.y * pointB2.x);
  const numeratorY =
    (pointA1.x * pointA2.y - pointA1.y * pointA2.x) * (pointB1.y - pointB2.y) -
    (pointA1.y - pointA2.y) * (pointB1.x * pointB2.y - pointB1.y * pointB2.x);

  return { x: numeratorX / denominator, y: numeratorY / denominator };
}

function detectXMarker(strokes) {
  if (!Array.isArray(strokes) || !strokes.length) {
    return { accepted: false, hint: "Draw X using two diagonal strokes." };
  }
  if (strokes.length === 1) {
    return { accepted: false, hint: "Draw the second diagonal stroke for X." };
  }

  const firstMetrics = getStrokeMetrics(strokes[0]?.points || []);
  const secondMetrics = getStrokeMetrics(strokes[1]?.points || []);
  if (!firstMetrics || !secondMetrics) {
    return { accepted: false, hint: "Draw two clear diagonal strokes for X." };
  }

  const firstDiagonal = getDiagonalDirection(firstMetrics);
  const secondDiagonal = getDiagonalDirection(secondMetrics);
  const bothLineLike = firstMetrics.lineRatio >= 0.6 && secondMetrics.lineRatio >= 0.6;
  const bothLargeEnough =
    Math.max(firstMetrics.width, firstMetrics.height) >= 0.22 &&
    Math.max(secondMetrics.width, secondMetrics.height) >= 0.22;
  const oppositeDiagonals = firstDiagonal !== 0 && secondDiagonal !== 0 && firstDiagonal !== secondDiagonal;
  const intersection = lineIntersection(firstMetrics.start, firstMetrics.end, secondMetrics.start, secondMetrics.end);
  const combinedMinX = Math.min(firstMetrics.minX, secondMetrics.minX);
  const combinedMaxX = Math.max(firstMetrics.maxX, secondMetrics.maxX);
  const combinedMinY = Math.min(firstMetrics.minY, secondMetrics.minY);
  const combinedMaxY = Math.max(firstMetrics.maxY, secondMetrics.maxY);
  const combinedWidth = Math.max(0.001, combinedMaxX - combinedMinX);
  const combinedHeight = Math.max(0.001, combinedMaxY - combinedMinY);
  const combinedCenterX = (combinedMinX + combinedMaxX) / 2;
  const combinedCenterY = (combinedMinY + combinedMaxY) / 2;
  const combinedDiagonal = Math.hypot(combinedWidth, combinedHeight);
  const intersectionInsideShape =
    intersection &&
    intersection.x >= combinedMinX - 0.08 &&
    intersection.x <= combinedMaxX + 0.08 &&
    intersection.y >= combinedMinY - 0.08 &&
    intersection.y <= combinedMaxY + 0.08;
  const crossesNearMiddle =
    intersectionInsideShape &&
    distance(intersection.x, intersection.y, combinedCenterX, combinedCenterY) <= Math.max(0.14, combinedDiagonal * 0.22);
  const centroidsCloseTogether =
    distance(firstMetrics.centroidX, firstMetrics.centroidY, secondMetrics.centroidX, secondMetrics.centroidY) <= Math.max(0.14, combinedDiagonal * 0.2);
  const centroidsNearShapeCenter =
    distance(firstMetrics.centroidX, firstMetrics.centroidY, combinedCenterX, combinedCenterY) <= Math.max(0.18, combinedDiagonal * 0.24) &&
    distance(secondMetrics.centroidX, secondMetrics.centroidY, combinedCenterX, combinedCenterY) <= Math.max(0.18, combinedDiagonal * 0.24);

  if (bothLineLike && bothLargeEnough && oppositeDiagonals && (crossesNearMiddle || (centroidsCloseTogether && centroidsNearShapeCenter))) {
    return { accepted: true };
  }

  return { accepted: false, hint: "Draw X as two diagonals that cross near the middle." };
}

function detectTicTacToeMarker(strokes, expectedMarker) {
  return expectedMarker === "O" ? detectOMarker(strokes) : detectXMarker(strokes);
}

function getTicTacToeTurnCopy(currentTurn, playerMarker, participantCount, winner, otherName) {
  if (winner === "draw") return "The board is full. Start another round?";
  if (winner === "O" || winner === "X") {
    return winner === playerMarker ? "You won the round." : `${otherName || "Your partner"} won the round.`;
  }
  if (participantCount < 2 && currentTurn !== playerMarker) {
    return "Waiting for another student to join Break Time.";
  }
  return currentTurn === playerMarker ? `Your turn. Draw ${playerMarker}.` : `${otherName || "Your partner"} is drawing ${currentTurn}.`;
}

function getTicTacToeMarkerGuide(marker) {
  return marker === "O" ? "Draw one smooth loop for O." : "Draw X as two diagonal strokes.";
}

function getTicTacToeWinnerName(ticTacToe, participants, ownerUserId) {
  if (!ticTacToe || !["O", "X"].includes(String(ticTacToe.winner || ""))) return "";
  const recentWinner = getParticipantName(ticTacToe.lastMoveBy, participants);
  if (ticTacToe.lastMoveBy && recentWinner && recentWinner !== "Student") return recentWinner;
  if (ticTacToe.winner === "O") return getParticipantNameByRole("owner", ownerUserId, participants);
  return getParticipantNameByRole("guest", ownerUserId, participants);
}

function createDotsEdgeId(orientation, row, col) {
  return `${orientation}-${row}-${col}`;
}

function createDotsBoxId(row, col) {
  return `box-${row}-${col}`;
}

function createEmptyDotsAndBoxesState(round = 1, updatedAt = "") {
  return {
    round,
    dotCount: DOTS_GRID_COUNT,
    edges: [],
    boxes: [],
    currentTurnRole: "owner",
    winnerRole: null,
    moveCount: 0,
    updatedAt,
    lastMoveBy: "",
    lastMoveEdgeId: ""
  };
}

function sanitizeDotsEdge(edge) {
  if (!edge || typeof edge !== "object") return null;
  const orientation = edge.orientation === "v" ? "v" : edge.orientation === "h" ? "h" : "";
  const row = Number(edge.row);
  const col = Number(edge.col);
  if (!orientation || !Number.isFinite(row) || !Number.isFinite(col)) return null;
  const role = edge.role === "guest" ? "guest" : "owner";
  return {
    id: String(edge.id || createDotsEdgeId(orientation, row, col)),
    orientation,
    row,
    col,
    role,
    userId: String(edge.userId || ""),
    userName: String(edge.userName || ""),
    color: String(edge.color || getParticipantColorByRole(role, "", [], getMarkerForRole(role))),
    createdAt: edge.createdAt || nowIso()
  };
}

function sanitizeDotsBox(box) {
  if (!box || typeof box !== "object") return null;
  const row = Number(box.row);
  const col = Number(box.col);
  if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
  const role = box.role === "guest" ? "guest" : "owner";
  return {
    id: String(box.id || createDotsBoxId(row, col)),
    row,
    col,
    role,
    userId: String(box.userId || ""),
    userName: String(box.userName || ""),
    color: String(box.color || getParticipantColorByRole(role, "", [], getMarkerForRole(role))),
    initial: String(box.initial || getParticipantInitial(box.userName)),
    createdAt: box.createdAt || nowIso()
  };
}

function normalizeDotsAndBoxesState(input) {
  const source = input && typeof input === "object" ? input : {};
  const dotCount = Math.max(4, Math.min(6, Number(source.dotCount || DOTS_GRID_COUNT)));
  const edgeMap = new Map();
  (Array.isArray(source.edges) ? source.edges : []).map(sanitizeDotsEdge).filter(Boolean).forEach((edge) => {
    edgeMap.set(edge.id, edge);
  });
  const boxMap = new Map();
  (Array.isArray(source.boxes) ? source.boxes : []).map(sanitizeDotsBox).filter(Boolean).forEach((box) => {
    boxMap.set(box.id, box);
  });
  const boxes = Array.from(boxMap.values());
  const maxBoxes = (dotCount - 1) * (dotCount - 1);
  const ownerScore = boxes.filter((box) => box.role === "owner").length;
  const guestScore = boxes.filter((box) => box.role === "guest").length;
  let winnerRole = null;
  if (boxes.length >= maxBoxes) {
    winnerRole = ownerScore === guestScore ? "draw" : ownerScore > guestScore ? "owner" : "guest";
  } else if (source.winnerRole === "draw" || source.winnerRole === "owner" || source.winnerRole === "guest") {
    winnerRole = source.winnerRole;
  }
  return {
    round: Math.max(1, Number(source.round || 1)),
    dotCount,
    edges: Array.from(edgeMap.values()),
    boxes,
    currentTurnRole: source.currentTurnRole === "guest" ? "guest" : "owner",
    winnerRole,
    moveCount: edgeMap.size,
    updatedAt: String(source.updatedAt || ""),
    lastMoveBy: String(source.lastMoveBy || ""),
    lastMoveEdgeId: String(source.lastMoveEdgeId || "")
  };
}

export function createInitialBreakTimeState() {
  return {
    game: TIC_TAC_TOE,
    updatedAt: "",
    ticTacToe: createEmptyTicTacToeState(1, ""),
    dotsAndBoxes: createEmptyDotsAndBoxesState(1, "")
  };
}

export function normalizeBreakTimeState(input) {
  const source = input && typeof input === "object" ? input : {};
  const game = source.game === DOTS_AND_BOXES ? DOTS_AND_BOXES : TIC_TAC_TOE;
  return {
    game,
    updatedAt: String(source.updatedAt || ""),
    ticTacToe: normalizeTicTacToeState(source.ticTacToe),
    dotsAndBoxes: normalizeDotsAndBoxesState(source.dotsAndBoxes)
  };
}

function getDotsGridPositions(dotCount) {
  const span = (100 - DOTS_GRID_PADDING * 2) / Math.max(1, dotCount - 1);
  return Array.from({ length: dotCount }, (_, index) => DOTS_GRID_PADDING + span * index);
}

function getDotsScores(boxes) {
  const list = Array.isArray(boxes) ? boxes : [];
  return {
    owner: list.filter((box) => box.role === "owner").length,
    guest: list.filter((box) => box.role === "guest").length
  };
}

function buildDotsEdgesMap(edges) {
  const map = new Map();
  (Array.isArray(edges) ? edges : []).forEach((edge) => {
    map.set(createDotsEdgeId(edge.orientation, edge.row, edge.col), edge);
  });
  return map;
}

function getAdjacentBoxesForEdge(orientation, row, col, dotCount) {
  const boxes = [];
  if (orientation === "h") {
    if (row > 0) boxes.push({ row: row - 1, col });
    if (row < dotCount - 1) boxes.push({ row, col });
  } else {
    if (col > 0) boxes.push({ row, col: col - 1 });
    if (col < dotCount - 1) boxes.push({ row, col });
  }
  return boxes.filter((box) => box.row >= 0 && box.col >= 0 && box.row < dotCount - 1 && box.col < dotCount - 1);
}

function isDotsBoxClosed(edgesMap, row, col) {
  return (
    edgesMap.has(createDotsEdgeId("h", row, col)) &&
    edgesMap.has(createDotsEdgeId("h", row + 1, col)) &&
    edgesMap.has(createDotsEdgeId("v", row, col)) &&
    edgesMap.has(createDotsEdgeId("v", row, col + 1))
  );
}

function nearestIndex(values, target) {
  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  values.forEach((value, index) => {
    const nextDistance = Math.abs(value - target);
    if (nextDistance < bestDistance) {
      bestDistance = nextDistance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

function detectDotsAndBoxesEdge(points, dotCount, edgesMap) {
  const metrics = getStrokeMetrics(points);
  if (!metrics) {
    return { accepted: false, hint: "Draw a short line between two nearby dots." };
  }

  const positions = getDotsGridPositions(dotCount).map((value) => value / 100);
  const step = positions.length > 1 ? positions[1] - positions[0] : 0.24;
  const horizontalDominant = metrics.width >= metrics.height * 1.15;
  const verticalDominant = metrics.height >= metrics.width * 1.15;
  const centerX = (metrics.minX + metrics.maxX) / 2;
  const centerY = (metrics.minY + metrics.maxY) / 2;

  if ((!horizontalDominant && !verticalDominant) || metrics.lineRatio < 0.56) {
    return { accepted: false, hint: "Use one straighter stroke between two dots." };
  }

  if (horizontalDominant) {
    const row = nearestIndex(positions, centerY);
    const midpoints = positions.slice(0, -1).map((value, index) => (value + positions[index + 1]) / 2);
    const col = nearestIndex(midpoints, centerX);
    const startX = positions[col];
    const endX = positions[col + 1];
    const targetY = positions[row];
    const spansSegment = metrics.minX <= startX + step * 0.18 && metrics.maxX >= endX - step * 0.18;
    const nearTarget = Math.abs(centerY - targetY) <= step * 0.22;
    const longEnough = metrics.width >= step * 0.34;
    const edgeId = createDotsEdgeId("h", row, col);
    if (spansSegment && nearTarget && longEnough) {
      if (edgesMap.has(edgeId)) {
        return { accepted: false, hint: "That line is already taken." };
      }
      return { accepted: true, orientation: "h", row, col };
    }
  }

  if (verticalDominant) {
    const col = nearestIndex(positions, centerX);
    const midpoints = positions.slice(0, -1).map((value, index) => (value + positions[index + 1]) / 2);
    const row = nearestIndex(midpoints, centerY);
    const startY = positions[row];
    const endY = positions[row + 1];
    const targetX = positions[col];
    const spansSegment = metrics.minY <= startY + step * 0.18 && metrics.maxY >= endY - step * 0.18;
    const nearTarget = Math.abs(centerX - targetX) <= step * 0.22;
    const longEnough = metrics.height >= step * 0.34;
    const edgeId = createDotsEdgeId("v", row, col);
    if (spansSegment && nearTarget && longEnough) {
      if (edgesMap.has(edgeId)) {
        return { accepted: false, hint: "That line is already taken." };
      }
      return { accepted: true, orientation: "v", row, col };
    }
  }

  return { accepted: false, hint: "Draw one short line that joins a pair of nearby dots." };
}

function BreakTimeModalShell({ title, helperCopy, onReset, onClose, children }) {
  return (
    <div className="break-time-overlay" role="dialog" aria-modal="true" aria-label="Break Time">
      <div className="break-time-modal">
        <div className="break-time-modal-header">
          <div>
            <div className="analysis-label">Break Time</div>
            <h3>{title}</h3>
            <p>{helperCopy}</p>
          </div>
          <div className="break-time-modal-actions">
            <button type="button" className="secondary-button" onClick={onReset}>
              New board
            </button>
            <button type="button" className="secondary-button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function BreakTimeWinnerBanner({ eyebrow, title, subtitle, onClose }) {
  return (
    <div className="break-time-winner-pop">
      <div className="break-time-winner-copy">
        <div className="break-time-winner-eyebrow">{eyebrow}</div>
        <div className="break-time-winner-title">{title}</div>
        <div className="break-time-winner-subtitle">{subtitle}</div>
      </div>
      <button type="button" className="break-time-winner-cta" onClick={onClose}>
        Back to study room
      </button>
    </div>
  );
}

function TicTacToeGame({ gameState, currentUserId, ownerUserId, participants, onSyncState, onClose }) {
  const ticTacToe = useMemo(() => normalizeTicTacToeState(gameState), [gameState]);
  const participantCount = Array.isArray(participants) ? participants.length : 0;
  const playerRole = getPlayerRole(ownerUserId, currentUserId);
  const playerMarker = getMarkerForRole(playerRole);
  const otherMarker = playerMarker === "O" ? "X" : "O";
  const currentUserName = getParticipantName(currentUserId, participants);
  const otherName = getParticipantNameByRole(playerRole === "owner" ? "guest" : "owner", ownerUserId, participants);
  const isPlayersTurn = ticTacToe.winner == null && ticTacToe.currentTurn === playerMarker;
  const winnerName = useMemo(
    () => getTicTacToeWinnerName(ticTacToe, participants, ownerUserId),
    [ownerUserId, participants, ticTacToe]
  );

  const [draftCellIndex, setDraftCellIndex] = useState(null);
  const [draftStrokes, setDraftStrokes] = useState([]);
  const [livePoints, setLivePoints] = useState([]);
  const [draftHint, setDraftHint] = useState("");
  const [showWinnerPopup, setShowWinnerPopup] = useState(false);

  const activePointerIdRef = useRef(null);
  const activeRectRef = useRef(null);
  const activeStrokePointsRef = useRef([]);
  const winnerBannerKeyRef = useRef("");

  useEffect(() => {
    setDraftCellIndex(null);
    setDraftStrokes([]);
    setLivePoints([]);
    setDraftHint("");
  }, [ticTacToe.updatedAt]);

  useEffect(() => {
    const nextKey = `${ticTacToe.round}:${ticTacToe.winner || "pending"}:${winnerName}`;
    if (ticTacToe.winner === "O" || ticTacToe.winner === "X") {
      if (winnerBannerKeyRef.current !== nextKey) {
        winnerBannerKeyRef.current = nextKey;
        setShowWinnerPopup(true);
      }
      return;
    }
    winnerBannerKeyRef.current = "";
    setShowWinnerPopup(false);
  }, [ticTacToe.round, ticTacToe.winner, winnerName]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  function updateSharedState(nextGameState) {
    if (typeof onSyncState !== "function") return;
    onSyncState({
      ...nextGameState,
      updatedAt: nowIso()
    });
  }

  function resetBoard() {
    setShowWinnerPopup(false);
    updateSharedState(createEmptyTicTacToeState(Math.max(1, Number(ticTacToe.round || 1)) + 1));
  }

  function normalizePoint(event) {
    const rect = activeRectRef.current;
    if (!rect?.width || !rect?.height) return null;
    return {
      x: clampNumber((event.clientX - rect.left) / rect.width, 0.04, 0.96),
      y: clampNumber((event.clientY - rect.top) / rect.height, 0.04, 0.96)
    };
  }

  function pushPoint(nextPoint) {
    const points = activeStrokePointsRef.current;
    const lastPoint = points[points.length - 1];
    if (lastPoint && distance(lastPoint.x, lastPoint.y, nextPoint.x, nextPoint.y) < 0.008) return;
    points.push(nextPoint);
    setLivePoints(points.flatMap((point) => [point.x, point.y]));
  }

  function commitMove(cellIndex, strokes) {
    if (ticTacToe.board[cellIndex] || ticTacToe.winner) return;
    const nextBoard = cloneBoard(ticTacToe.board);
    nextBoard[cellIndex] = {
      marker: playerMarker,
      userId: currentUserId,
      userName: currentUserName,
      color: getParticipantColorByRole(playerRole, ownerUserId, participants, playerMarker),
      createdAt: nowIso(),
      strokes: strokes.map(sanitizeStroke).filter((stroke) => stroke.points.length >= 4)
    };
    const winnerState = computeTicTacToeWinner(nextBoard);
    updateSharedState({
      round: ticTacToe.round,
      board: nextBoard,
      currentTurn: winnerState.winner ? ticTacToe.currentTurn : otherMarker,
      winner: winnerState.winner,
      winningLine: winnerState.winningLine,
      moveCount: nextBoard.filter(Boolean).length,
      lastMoveBy: currentUserId,
      lastMoveCell: cellIndex
    });
  }

  function finalizeStroke(cellIndex) {
    const rawPoints = activeStrokePointsRef.current.flatMap((point) => [point.x, point.y]);
    activePointerIdRef.current = null;
    activeRectRef.current = null;
    activeStrokePointsRef.current = [];
    setLivePoints([]);
    if (rawPoints.length < 4) return;

    const nextStroke = { points: rawPoints };
    const nextStrokes = playerMarker === "X"
      ? (
        draftCellIndex === cellIndex && draftStrokes.length === 1
          ? [...draftStrokes, nextStroke]
          : [nextStroke]
      )
      : [nextStroke];
    setDraftStrokes(nextStrokes);
    const detection = detectTicTacToeMarker(nextStrokes, playerMarker);
    if (detection.accepted) {
      commitMove(cellIndex, nextStrokes);
      setDraftCellIndex(null);
      setDraftStrokes([]);
      setDraftHint("");
      return;
    }
    setDraftCellIndex(cellIndex);
    setDraftHint(detection.hint || getTicTacToeMarkerGuide(playerMarker));
  }

  function handlePointerDown(cellIndex, event) {
    if (!isPlayersTurn || ticTacToe.board[cellIndex] || ticTacToe.winner || participantCount < 1) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (draftCellIndex !== null && draftCellIndex !== cellIndex) {
      setDraftStrokes([]);
      setDraftHint("");
    }
    setDraftCellIndex(cellIndex);
    activePointerIdRef.current = event.pointerId;
    activeRectRef.current = event.currentTarget.getBoundingClientRect();
    activeStrokePointsRef.current = [];
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (err) {
      // Ignore capture failures.
    }
    const point = normalizePoint(event);
    if (!point) return;
    pushPoint(point);
    setDraftHint("");
  }

  function handlePointerMove(event) {
    if (activePointerIdRef.current !== event.pointerId) return;
    const point = normalizePoint(event);
    if (!point) return;
    pushPoint(point);
  }

  function handlePointerUp(cellIndex, event) {
    if (activePointerIdRef.current !== event.pointerId) return;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (err) {
      // Ignore release failures.
    }
    finalizeStroke(cellIndex);
  }

  function handlePointerCancel(event) {
    if (activePointerIdRef.current !== event.pointerId) return;
    activePointerIdRef.current = null;
    activeRectRef.current = null;
    activeStrokePointsRef.current = [];
    setLivePoints([]);
  }

  const helperCopy = getTicTacToeTurnCopy(ticTacToe.currentTurn, playerMarker, participantCount, ticTacToe.winner, otherName);
  const guideCopy = getTicTacToeMarkerGuide(playerMarker);
  const winningSet = new Set(Array.isArray(ticTacToe.winningLine) ? ticTacToe.winningLine : []);

  return (
    <BreakTimeModalShell title="Ink break" helperCopy={helperCopy} onReset={resetBoard} onClose={onClose}>
      <div className="break-time-meta-row">
        <div className="break-time-player-chip is-self">
          <span className="break-time-player-marker">{playerMarker}</span>
          <span>{currentUserName || "You"}</span>
          <span className="collab-self-pill">You</span>
        </div>
        <div className="break-time-player-chip">
          <span className="break-time-player-marker">{otherMarker}</span>
          <span>{otherName || "Partner"}</span>
        </div>
        <div className="break-time-note">{guideCopy}</div>
      </div>

      {showWinnerPopup && winnerName && (
        <BreakTimeWinnerBanner
          eyebrow="Round complete"
          title={<><strong>{winnerName}</strong> takes the win</>}
          subtitle="Nice break. Lets go back to study room."
          onClose={onClose}
        />
      )}

      <div className="break-time-board-shell">
        <div className="break-time-board" role="grid" aria-label="Shared tic tac toe board">
          <div className="break-time-board-grid" aria-hidden="true">
            <span className="break-time-grid-line vertical line-1" />
            <span className="break-time-grid-line vertical line-2" />
            <span className="break-time-grid-line horizontal line-1" />
            <span className="break-time-grid-line horizontal line-2" />
          </div>
          {ticTacToe.board.map((cell, cellIndex) => {
            const isActiveDraftCell = draftCellIndex === cellIndex;
            const committedStrokes = Array.isArray(cell?.strokes) ? cell.strokes : [];
            const showDraft = isActiveDraftCell && draftStrokes.length > 0;
            const showLiveStroke = isActiveDraftCell && livePoints.length >= 4;
            return (
              <div
                key={`ttt-cell-${cellIndex}`}
                className={`break-time-cell ${winningSet.has(cellIndex) ? "is-winning" : ""} ${isPlayersTurn && !cell && !ticTacToe.winner ? "is-playable" : ""}`.trim()}
                role="gridcell"
              >
                <div
                  className="break-time-cell-surface"
                  onPointerDown={(event) => handlePointerDown(cellIndex, event)}
                  onPointerMove={handlePointerMove}
                  onPointerUp={(event) => handlePointerUp(cellIndex, event)}
                  onPointerCancel={handlePointerCancel}
                >
                  <svg viewBox="0 0 100 100" className="break-time-cell-svg" aria-hidden="true">
                    {committedStrokes.map((stroke, strokeIndex) => (
                      <path
                        key={`stroke-${strokeIndex}`}
                        d={buildStrokePath(stroke.points)}
                        fill="none"
                        stroke={cell?.color || getMarkerColor(cell?.marker || "O")}
                        strokeWidth={COMMITTED_STROKE_WIDTH}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    ))}
                    {showDraft && draftStrokes.map((stroke, strokeIndex) => (
                      <path
                        key={`draft-${strokeIndex}`}
                        d={buildStrokePath(stroke.points)}
                        fill="none"
                        stroke={getDraftStrokeColor(playerMarker, 0.58)}
                        strokeWidth={DRAFT_STROKE_WIDTH}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    ))}
                    {showLiveStroke && (
                      <path
                        d={buildStrokePath(livePoints)}
                        fill="none"
                        stroke={getDraftStrokeColor(playerMarker, 0.88)}
                        strokeWidth={LIVE_STROKE_WIDTH}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="break-time-footer">
        <div className={`break-time-hint ${draftHint ? "active" : ""}`.trim()}>
          {draftHint || (isPlayersTurn ? "Tap a square and draw your mark with the stylus." : "Watch the shared board update live while your partner draws.")}
        </div>
        <div className="break-time-footer-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              setDraftCellIndex(null);
              setDraftStrokes([]);
              setLivePoints([]);
              setDraftHint("");
            }}
            disabled={draftCellIndex === null && !draftStrokes.length}
          >
            Clear draft
          </button>
        </div>
      </div>
    </BreakTimeModalShell>
  );
}

function DotsAndBoxesGame({ gameState, currentUserId, ownerUserId, participants, onSyncState, onClose }) {
  const dotsState = useMemo(() => normalizeDotsAndBoxesState(gameState), [gameState]);
  const playerRole = getPlayerRole(ownerUserId, currentUserId);
  const otherRole = playerRole === "owner" ? "guest" : "owner";
  const currentUserName = getParticipantNameByRole(playerRole, ownerUserId, participants);
  const otherName = getParticipantNameByRole(otherRole, ownerUserId, participants);
  const dotPositions = useMemo(() => getDotsGridPositions(dotsState.dotCount), [dotsState.dotCount]);
  const scores = useMemo(() => getDotsScores(dotsState.boxes), [dotsState.boxes]);
  const isPlayersTurn = dotsState.winnerRole == null && dotsState.currentTurnRole === playerRole;
  const winnerName = dotsState.winnerRole === "draw" ? "" : getParticipantNameByRole(dotsState.winnerRole || "", ownerUserId, participants);

  const [draftHint, setDraftHint] = useState("");
  const [livePoints, setLivePoints] = useState([]);
  const [showWinnerPopup, setShowWinnerPopup] = useState(false);

  const activePointerIdRef = useRef(null);
  const activeRectRef = useRef(null);
  const activeStrokePointsRef = useRef([]);
  const winnerBannerKeyRef = useRef("");

  useEffect(() => {
    setDraftHint("");
    setLivePoints([]);
  }, [dotsState.updatedAt]);

  useEffect(() => {
    const nextKey = `${dotsState.round}:${dotsState.winnerRole || "pending"}:${winnerName}`;
    if (dotsState.winnerRole === "owner" || dotsState.winnerRole === "guest" || dotsState.winnerRole === "draw") {
      if (winnerBannerKeyRef.current !== nextKey) {
        winnerBannerKeyRef.current = nextKey;
        setShowWinnerPopup(true);
      }
      return;
    }
    winnerBannerKeyRef.current = "";
    setShowWinnerPopup(false);
  }, [dotsState.round, dotsState.winnerRole, winnerName]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  function updateSharedState(nextState) {
    if (typeof onSyncState !== "function") return;
    onSyncState({
      ...nextState,
      updatedAt: nowIso()
    });
  }

  function resetBoard() {
    setShowWinnerPopup(false);
    updateSharedState(createEmptyDotsAndBoxesState(Math.max(1, Number(dotsState.round || 1)) + 1));
  }

  function normalizePoint(event) {
    const rect = activeRectRef.current;
    if (!rect?.width || !rect?.height) return null;
    return {
      x: clampNumber((event.clientX - rect.left) / rect.width, 0, 1),
      y: clampNumber((event.clientY - rect.top) / rect.height, 0, 1)
    };
  }

  function pushPoint(nextPoint) {
    const points = activeStrokePointsRef.current;
    const lastPoint = points[points.length - 1];
    if (lastPoint && distance(lastPoint.x, lastPoint.y, nextPoint.x, nextPoint.y) < 0.01) return;
    points.push(nextPoint);
    setLivePoints(points.flatMap((point) => [point.x, point.y]));
  }

  function commitEdge(result) {
    const existingEdges = Array.isArray(dotsState.edges) ? dotsState.edges : [];
    const nextEdges = [
      ...existingEdges,
      {
        id: createDotsEdgeId(result.orientation, result.row, result.col),
        orientation: result.orientation,
        row: result.row,
        col: result.col,
        role: playerRole,
        userId: currentUserId,
        userName: currentUserName,
        color: getParticipantColorByRole(playerRole, ownerUserId, participants, getMarkerForRole(playerRole)),
        createdAt: nowIso()
      }
    ];

    const edgesMap = buildDotsEdgesMap(nextEdges);
    const existingBoxIds = new Set((Array.isArray(dotsState.boxes) ? dotsState.boxes : []).map((box) => box.id));
    const closedBoxes = [];
    getAdjacentBoxesForEdge(result.orientation, result.row, result.col, dotsState.dotCount).forEach((box) => {
      const boxId = createDotsBoxId(box.row, box.col);
      if (existingBoxIds.has(boxId)) return;
      if (!isDotsBoxClosed(edgesMap, box.row, box.col)) return;
      closedBoxes.push({
        id: boxId,
        row: box.row,
        col: box.col,
        role: playerRole,
        userId: currentUserId,
        userName: currentUserName,
        color: getParticipantColorByRole(playerRole, ownerUserId, participants, getMarkerForRole(playerRole)),
        initial: getParticipantInitial(currentUserName),
        createdAt: nowIso()
      });
    });

    const nextBoxes = [...(Array.isArray(dotsState.boxes) ? dotsState.boxes : []), ...closedBoxes];
    const maxBoxes = (dotsState.dotCount - 1) * (dotsState.dotCount - 1);
    const nextScores = getDotsScores(nextBoxes);
    const boardFilled = nextBoxes.length >= maxBoxes;
    const winnerRole = boardFilled
      ? nextScores.owner === nextScores.guest
        ? "draw"
        : nextScores.owner > nextScores.guest
          ? "owner"
          : "guest"
      : null;

    updateSharedState({
      round: dotsState.round,
      dotCount: dotsState.dotCount,
      edges: nextEdges,
      boxes: nextBoxes,
      currentTurnRole: closedBoxes.length ? playerRole : otherRole,
      winnerRole,
      moveCount: nextEdges.length,
      lastMoveBy: currentUserId,
      lastMoveEdgeId: createDotsEdgeId(result.orientation, result.row, result.col)
    });
  }

  function finalizeStroke() {
    const rawPoints = activeStrokePointsRef.current.flatMap((point) => [point.x, point.y]);
    activePointerIdRef.current = null;
    activeRectRef.current = null;
    activeStrokePointsRef.current = [];
    setLivePoints([]);
    if (rawPoints.length < 4) return;
    const edgesMap = buildDotsEdgesMap(dotsState.edges);
    const detection = detectDotsAndBoxesEdge(rawPoints, dotsState.dotCount, edgesMap);
    if (!detection.accepted) {
      setDraftHint(detection.hint || "Draw one short line between two dots.");
      return;
    }
    setDraftHint("");
    commitEdge(detection);
  }

  function handlePointerDown(event) {
    if (!isPlayersTurn || dotsState.winnerRole) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    activePointerIdRef.current = event.pointerId;
    activeRectRef.current = event.currentTarget.getBoundingClientRect();
    activeStrokePointsRef.current = [];
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (err) {
      // Ignore capture failures.
    }
    const point = normalizePoint(event);
    if (!point) return;
    pushPoint(point);
    setDraftHint("");
  }

  function handlePointerMove(event) {
    if (activePointerIdRef.current !== event.pointerId) return;
    const point = normalizePoint(event);
    if (!point) return;
    pushPoint(point);
  }

  function handlePointerUp(event) {
    if (activePointerIdRef.current !== event.pointerId) return;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (err) {
      // Ignore release failures.
    }
    finalizeStroke();
  }

  function handlePointerCancel(event) {
    if (activePointerIdRef.current !== event.pointerId) return;
    activePointerIdRef.current = null;
    activeRectRef.current = null;
    activeStrokePointsRef.current = [];
    setLivePoints([]);
  }

  const helperCopy = dotsState.winnerRole === "draw"
    ? "The board is complete. Great rally."
    : dotsState.winnerRole
      ? `${winnerName || "A player"} wins the board.`
      : isPlayersTurn
        ? "Your turn. Draw one line between two dots."
        : `${otherName || "Your partner"} is drawing the next edge.`;

  return (
    <BreakTimeModalShell title="Dots & Boxes" helperCopy={helperCopy} onReset={resetBoard} onClose={onClose}>
      <div className="break-time-meta-row dots-boxes-score-row">
        <div className="break-time-player-chip is-self">
          <span className="break-time-player-marker">{getParticipantInitial(currentUserName)}</span>
          <span>{currentUserName || "You"}</span>
          <span className="collab-self-pill">You</span>
          <span className="break-time-score-pill">{scores[playerRole]}</span>
        </div>
        <div className="break-time-player-chip">
          <span className="break-time-player-marker">{getParticipantInitial(otherName)}</span>
          <span>{otherName || "Partner"}</span>
          <span className="break-time-score-pill">{scores[otherRole]}</span>
        </div>
        <div className="break-time-note">Draw one straight line from dot to dot. Close a box to keep the turn.</div>
      </div>

      {showWinnerPopup && (
        <BreakTimeWinnerBanner
          eyebrow={dotsState.winnerRole === "draw" ? "Board complete" : "Notebook champion"}
          title={
            dotsState.winnerRole === "draw"
              ? "Its a draw"
              : <><strong>{winnerName}</strong> closes the board</>
          }
          subtitle="Nice break. Lets go back to study room."
          onClose={onClose}
        />
      )}

      <div className="break-time-board-shell dots-boxes-shell">
        <div className="dots-boxes-board-wrap">
          <svg
            viewBox="0 0 100 100"
            className={`break-time-dots-svg ${isPlayersTurn && !dotsState.winnerRole ? "is-playable" : ""}`.trim()}
            role="img"
            aria-label="Shared dots and boxes board"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          >
            {dotsState.boxes.map((box) => {
              const x = dotPositions[box.col];
              const y = dotPositions[box.row];
              const size = dotPositions[box.col + 1] - dotPositions[box.col];
              return (
                <g key={box.id}>
                  <rect
                    x={x + 1.8}
                    y={y + 1.8}
                    width={size - 3.6}
                    height={size - 3.6}
                    rx="4"
                    fill={box.role === "owner" ? "rgba(37, 99, 235, 0.12)" : "rgba(15, 23, 42, 0.1)"}
                  />
                  <text
                    x={x + size / 2}
                    y={y + size / 2 + 2}
                    textAnchor="middle"
                    className="dots-boxes-box-letter"
                    fill={box.color || getParticipantColorByRole(box.role, ownerUserId, participants, getMarkerForRole(box.role))}
                  >
                    {box.initial}
                  </text>
                </g>
              );
            })}

            {dotsState.edges.map((edge) => {
              const startX = edge.orientation === "h" ? dotPositions[edge.col] : dotPositions[edge.col];
              const endX = edge.orientation === "h" ? dotPositions[edge.col + 1] : dotPositions[edge.col];
              const startY = edge.orientation === "h" ? dotPositions[edge.row] : dotPositions[edge.row];
              const endY = edge.orientation === "h" ? dotPositions[edge.row] : dotPositions[edge.row + 1];
              return (
                <line
                  key={edge.id}
                  x1={startX}
                  y1={startY}
                  x2={endX}
                  y2={endY}
                  stroke={edge.color || getParticipantColorByRole(edge.role, ownerUserId, participants, getMarkerForRole(edge.role))}
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
              );
            })}

            {livePoints.length >= 4 && (
              <path
                d={buildStrokePath(livePoints)}
                fill="none"
                stroke={getDraftStrokeColor(getMarkerForRole(playerRole), 0.78)}
                strokeWidth={LIVE_STROKE_WIDTH}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {dotPositions.map((y, rowIndex) =>
              dotPositions.map((x, colIndex) => (
                <circle
                  key={`dot-${rowIndex}-${colIndex}`}
                  cx={x}
                  cy={y}
                  r="1.15"
                  className="dots-boxes-dot"
                />
              ))
            )}
          </svg>
        </div>
      </div>

      <div className="break-time-footer">
        <div className={`break-time-hint ${draftHint ? "active" : ""}`.trim()}>
          {draftHint || (isPlayersTurn ? "Draw one short line that joins a pair of nearby dots." : "Watch the board update live while your partner connects dots.")}
        </div>
      </div>
    </BreakTimeModalShell>
  );
}

export default function BreakTimeGamesModal({
  open,
  onClose,
  gameState,
  currentUserId,
  ownerUserId,
  participants,
  onSyncState
}) {
  const normalizedState = useMemo(() => normalizeBreakTimeState(gameState), [gameState]);

  function syncGameState(nextPatch) {
    if (typeof onSyncState !== "function") return;
    onSyncState({
      ...normalizedState,
      ...nextPatch,
      updatedAt: nowIso()
    });
  }

  if (!open) return null;

  if (normalizedState.game === DOTS_AND_BOXES) {
    return (
      <DotsAndBoxesGame
        gameState={normalizedState.dotsAndBoxes}
        currentUserId={currentUserId}
        ownerUserId={ownerUserId}
        participants={participants}
        onSyncState={(nextDotsState) => syncGameState({ game: DOTS_AND_BOXES, dotsAndBoxes: nextDotsState })}
        onClose={onClose}
      />
    );
  }

  return (
    <TicTacToeGame
      gameState={normalizedState.ticTacToe}
      currentUserId={currentUserId}
      ownerUserId={ownerUserId}
      participants={participants}
      onSyncState={(nextTicTacToe) => syncGameState({ game: TIC_TAC_TOE, ticTacToe: nextTicTacToe })}
      onClose={onClose}
    />
  );
}
